/**
 * CookieBanner — one-time consent card, bottom-right.
 *
 * Appears on first visit. The choice ("accepted" | "rejected") is
 * stored in `localStorage` under `cl-cookie-consent` and the banner
 * never shows again for twelve months.
 *
 * CanvaLoop currently sets only strictly-necessary storage (auth token,
 * theme, consent itself), so "reject" and "accept" behave the same
 * today. The banner exists so that when we do add analytics / non-
 * essential storage, the consent gate is already in place and we can
 * check `hasCookieConsent('analytics')` before loading anything.
 *
 * Keyboard: Tab-focusable Accept / Reject / Policy links. Esc dismisses
 * the banner without changing the stored choice (shows again next
 * session).
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Check, X } from "lucide-react";
import { ROUTES } from "@/types";

const STORAGE_KEY = "cl-cookie-consent";
const CONSENT_TTL_DAYS = 365;

interface ConsentRecord {
  /** "accepted" | "rejected" — strictly-necessary always allowed either way. */
  choice: "accepted" | "rejected";
  ts: number;
}

function readConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (!parsed.choice || !parsed.ts) return null;
    const ageDays = (Date.now() - parsed.ts) / (1000 * 60 * 60 * 24);
    if (ageDays > CONSENT_TTL_DAYS) return null;
    return parsed;
  } catch { return null; }
}

function writeConsent(choice: ConsentRecord["choice"]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, ts: Date.now() }));
  } catch { /* storage disabled; silent */ }
}

/** Called by future analytics / heatmap loaders before they fire. */
export function hasCookieConsent(category: "essential" | "analytics"): boolean {
  if (category === "essential") return true;
  return readConsent()?.choice === "accepted";
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer to idle so it never competes with first paint.
    const decide = () => setVisible(readConsent() === null);
    const ric = (window as unknown as { requestIdleCallback?: (fn: () => void) => number }).requestIdleCallback;
    if (ric) ric(decide); else window.setTimeout(decide, 400);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setVisible(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!visible) return null;

  const accept = () => { writeConsent("accepted"); setVisible(false); };
  const reject = () => { writeConsent("rejected"); setVisible(false); };

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      style={{
        position: "fixed",
        bottom: "clamp(16px, 3vw, 28px)",
        right:  "clamp(16px, 3vw, 28px)",
        maxWidth: 380,
        width: "calc(100vw - 32px)",
        zIndex: 2147483645,
        background: "var(--bg-surface-raised)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-xl)",
        boxShadow: "0 24px 60px -24px rgba(0,0,0,0.45)",
        padding: 18,
        animation: "fadeInUp 320ms var(--ease-smooth)",
        color: "var(--text-primary)",
      }}
    >
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 10,
      }}>
        <span style={{
          width: 30, height: 30,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          borderRadius: "var(--radius-md)",
          background: "var(--accent-50)",
          color: "var(--accent-300)",
        }}>
          <Cookie size={15} strokeWidth={1.75} />
        </span>
        <strong style={{
          fontSize: "var(--font-size-sm)", fontWeight: 600,
          letterSpacing: 0,
        }}>
          A small note on cookies
        </strong>
      </div>

      <p style={{
        margin: 0,
        fontSize: "var(--font-size-xs)",
        lineHeight: 1.55,
        color: "var(--text-secondary)",
        marginBottom: 14,
      }}>
        We use strictly-necessary cookies only.{" "}
        <Link to={ROUTES.COOKIES} style={{
          color: "var(--accent-300)",
          textDecoration: "underline",
          textUnderlineOffset: 2,
        }}>
          Cookie policy
        </Link>
      </p>

      <div style={{
        display: "flex",
        gap: 8,
      }}>
        <button
          onClick={reject}
          style={{
            flex: 1,
            height: 36,
            display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            gap: 6,
            padding: "0 12px",
            background: "var(--bg-surface-alt)",
            color: "var(--text-secondary)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            fontSize: "var(--font-size-xs)", fontWeight: 600,
            cursor: "pointer",
            transition: "var(--transition-fast)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--bg-surface)";
            e.currentTarget.style.color = "var(--text-primary)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--bg-surface-alt)";
            e.currentTarget.style.color = "var(--text-secondary)";
          }}
        >
          <X size={12} strokeWidth={2} />
          Reject non-essential
        </button>
        <button
          onClick={accept}
          style={{
            flex: 1,
            height: 36,
            display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            gap: 6,
            padding: "0 12px",
            background: "var(--brand-gradient)",
            color: "#fff",
            border: "none",
            borderRadius: "var(--radius-md)",
            fontSize: "var(--font-size-xs)", fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 10px 24px -12px rgba(255,77,109,0.55)",
            transition: "transform 160ms var(--ease-smooth)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
        >
          <Check size={12} strokeWidth={2} />
          Accept all
        </button>
      </div>
    </div>
  );
}
